import logError from 'src/data/services/logError';
import React, { useCallback, useState } from 'react';
import { Text, View, FlatList, RefreshControl, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import Loader from 'src/presentation/components/atoms/Loader';
import { MaterialIcons } from '@expo/vector-icons';
import dayjs from 'dayjs';
import NavBar from 'src/presentation/components/organisms/NavBar';
import MessagesEmptyState from 'src/presentation/components/molecules/MessagesEmptyState';
import Card from 'src/presentation/components/atoms/Card';
import Translations from 'src/core/utils/Translations';
import _ from 'lodash';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useNotificationStore } from 'src/domain/stores/useNotificationStore';
import { navigatePage } from 'src/presentation/helpers/NavigationHelper';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';

interface PushNotification {
	id: string | number;
	event_uuid: string;
	execute_time: string;
	message: string;
	event: string;
	link?: string;
	[key: string]: unknown;
}

function MessagesScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const eventStore = useEventStore();
	const navigationStore = useNavigationStore();
	const notificationStore = useNotificationStore();

	const [searchTerm, setSearchTerm] = useState('');
	const [refreshing, setRefreshing] = useState(false);

	// Opening the tab snapshots the read-state + clears the badge (store-side), so new messages still look
	// unread this visit and the styling fades on refresh / next visit.
	useFocusEffect(
		useCallback(() => {
			useNotificationStore.getState().openMessages().catch(logError);
		}, []),
	);

	const onRefresh = () => {
		setRefreshing(true);
		useNotificationStore
			.getState()
			.refreshMessages()
			.catch(logError)
			.finally(() => setRefreshing(false));
	};

	// Only store the term; the visible list derives from the live notifications below, so it stays in sync after
	// a focus-refresh or pull-to-refresh instead of freezing on a one-off snapshot.
	const searchEvent = (term: string) => {
		setSearchTerm(term);
	};

	const query = searchTerm.trim().toLowerCase();
	const filtered = query.length > 0;
	const data: PushNotification[] = filtered
		? notificationStore.notifications.filter(
				(n: PushNotification) => n.message.toLowerCase().includes(query) || n.event.toLowerCase().includes(query),
			)
		: notificationStore.notifications;

	const navigateTo = (IDPath: string | number) => {
		const pageIDs = typeof IDPath === 'string' ? IDPath.split('/') : new Array(IDPath.toString());

		const page = eventStore.getPageByID(pageIDs);
		if (page && typeof page.type === 'string') {
			eventStore.updateTheme(page.event_uuid);
			eventStore.activeEventUuid = page.event_uuid;
			navigatePage(navigation, page.type, page);
		}
		navigationStore.setNotificationOrigin('');
	};

	// Only notifications with a link have a destination (the linked content page); link-less notifications are
	// informational and do nothing on press.
	const onButtonPress = (item: PushNotification) => {
		if (!item.link || eventStore.isLoading) return;
		navigationStore.setActiveEventByUuid(item.event_uuid);
		navigateTo(item.link);
	};

	const renderMessage = ({ item }: { item: PushNotification }) => {
		const niceDate = dayjs(item.execute_time).format(Constants.dateFormat);
		const read = notificationStore.seenAtOpen.includes(item.id);
		return (
			<TouchableOpacity
				key={item.id}
				style={read ? { ...styles.listItem, ...styles.listItemRead } : styles.listItem}
				onPress={_.debounce(() => onButtonPress(item), 800, {
					leading: true,
					trailing: false,
				})}
			>
				<View style={styles.body}>
					<Text style={read ? styles.listItemTitle : { ...styles.listItemTitle, ...styles.listItemTitleUnRead }}>
						{item.event}
					</Text>
					<Text style={styles.listItemDescription}>{item.message}</Text>
					<View style={styles.messageDate}>
						<Text style={styles.listItemDate}>
							{niceDate} {item.link && <MaterialIcons name="keyboard-arrow-right" style={styles.arrow} />}
						</Text>
					</View>
				</View>
			</TouchableOpacity>
		);
	};

	// Only rendered as the list's ListEmptyComponent, so there is nothing to list by definition.
	// Why there is nothing differs per platform, which is what the component answers.
	const renderEmpty = () => (
		<Card>
			<MessagesEmptyState searching={filtered} />
		</Card>
	);

	return (
		<View style={styles.flex1}>
			<NavBar
				backBtn={false}
				bgcolor={Colors.primary}
				centerTitle={true}
				color={Colors.black}
				editBtn={false}
				iosPlaceholder={Translations.messageSearchPlaceholder}
				searchBtn={true}
				title={Translations.messageDefaultTitle}
				onSearchTermChange={(term: string) => searchEvent(term)}
			/>
			<View style={styles.flex1}>
				{notificationStore.isLoading && !filtered && notificationStore.notifications.length === 0 ? (
					<Loader />
				) : (
					<FlatList
						ListEmptyComponent={renderEmpty}
						contentContainerStyle={data.length === 0 ? styles.flexGrow : undefined}
						data={data}
						keyExtractor={(item) => item.id.toString()}
						refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
						renderItem={renderMessage}
					/>
				)}
			</View>
		</View>
	);
}

export default MessagesScreen;

const styles = StyleSheet.create({
	flex1: {
		flex: 1,
	},
	flexGrow: {
		flexGrow: 1,
	},
	listItem: {
		backgroundColor: Colors.white,
		paddingRight: Dimens.padding10,
		height: 78,
		marginLeft: Dimens.margin0,
		flexDirection: 'row',
		padding: Dimens.padding16,
		borderBottomWidth: 1,
		borderBottomColor: Colors.greyDivider,
	},
	listItemRead: {
		opacity: 0.5,
	},
	listItemTitle: {
		paddingLeft: Dimens.padding10,
		fontSize: Platform.OS === 'ios' ? Dimens.fontSize17 : Dimens.fontSize16,
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratBold,
		paddingBottom: Dimens.padding2,
	},
	listItemTitleUnRead: {
		color: Colors.greyHeader,
	},
	listItemDescription: {
		paddingLeft: Dimens.padding10,
		color: Colors.greyText,
		fontFamily: Fonts.montserratRegular,
		fontSize: Platform.OS === 'ios' ? Dimens.fontSize17 : Dimens.fontSize16,
	},
	listItemDate: {
		color: Colors.greyText,
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize12,
	},
	messageDate: {
		position: 'absolute',
		right: 0,
		top: 0,
	},
	arrow: {
		paddingTop: Dimens.padding6,
		fontSize: Dimens.fontSize14,
		color: Colors.greyIcon,
	},
	body: {
		flex: 1,
		justifyContent: 'center',
	},
});
